import { useMemo } from 'react';
import { getTextWidth } from '../utils';
import { MAX_Y_AXIS_TICK_WIDTH_STRING } from '../constants/utils';

interface IProps {
  label?: string;
  padding?: number;
  font?: string;
}

// Calculate vertical text width
export const calculateVerticalTextWidth = (
  text: string,
  font: string = '10px Geist',
): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = font;
    const metrics = context.measureText(text);
    // This gets the baseline of a character + the height of a character
    return metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
  }
  return 0;
};

// Calculate the width of the Y-axis based on the label
const calculateYAxisWidth = (
  label: string,
  font: string = '10px Geist',
  padding: number = 16,
): number => {
  // Calculate the width of largest Y-axis tick
  const maxYAxisTickWidth = getTextWidth(MAX_Y_AXIS_TICK_WIDTH_STRING, font);

  // Calculate the vertical width (height) of the label if it exists, otherwise set to 0
  const labelVerticalWidth = label
    ? calculateVerticalTextWidth(label, font)
    : 0;

  // Return the maximum width plus padding
  return label
    ? labelVerticalWidth + maxYAxisTickWidth + padding
    : maxYAxisTickWidth;
};

// Hook to use the Y-axis width spacing calculation
const useYAxisWidth = ({ label = '', font, padding }: IProps = {}): number =>
  useMemo(
    () => calculateYAxisWidth(label, font, padding),
    [label, font, padding],
  );

export default useYAxisWidth;
